import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  orderBy,
  query,
  where,
  serverTimestamp,
  Timestamp,
  runTransaction,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../../../firebase/config';
import {
  CancellationReason,
  DamagedItem,
  DamagedItemRecord,
  Order,
  OrderFormData,
  OrderItem,
  ProductStockAvailability,
  ReturnReason,
  WarehouseStockInfo,
} from '../../../enums/order';
import { ProductService } from '../product/productService';
import { WarehouseService } from '../warehouse/warehouseService';
import { WarehouseInventoryService } from '../product/WarehouseInventoryService';

const ORDERS_COLLECTION = 'orders';
const DAMAGED_ITEMS_COLLECTION = 'damaged_items';

export interface PaginatedOrdersResult {
  orders: Order[];
  totalCount: number;
  // Kept for interface compatibility — always null now (seek-based pagination
  // doesn't need callers to pass cursors back in).
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export interface ItemWarehouseAllocation {
  itemIndex: number;
  productId: string;
  variantId?: string;
  productName: string;
  variantName?: string;
  totalQuantity: number;
  allocations: {
    warehouseId: string;
    warehouseName: string;
    quantity: number;
    availableStock: number;
  }[];
}

const cleanForFirestore = (data: any): any => {
  if (data === null || data === undefined) return null;
  if (Array.isArray(data)) return data.map(item => cleanForFirestore(item));
  if (data instanceof Date) return Timestamp.fromDate(data);
  if (data instanceof Timestamp) return data;
  if (typeof data === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(data)) {
      cleaned[key] = value !== undefined ? cleanForFirestore(value) : null;
    }
    return cleaned;
  }
  return data;
};

export class OrderService {

  private static mapDocToOrder(docSnap: QueryDocumentSnapshot<DocumentData>): Order {
    const data = docSnap.data();

    const toDate = (timestamp: any): Date | undefined => {
      if (!timestamp) return undefined;
      if (timestamp.toDate && typeof timestamp.toDate === 'function') return timestamp.toDate();
      if (timestamp instanceof Date) return timestamp;
      if (timestamp.seconds !== undefined) return new Date(timestamp.seconds * 1000);
      if (typeof timestamp === 'string' || typeof timestamp === 'number') {
        const d = new Date(timestamp);
        if (!isNaN(d.getTime())) return d;
      }
      return undefined;
    };

    const order: any = {
      id: docSnap.id,
      orderNumber: data.orderNumber || '',
      customerName: data.customerName || '',
      customerEmail: data.customerEmail || '',
      customerPhone: data.customerPhone || '',
      customerWhatsApp: data.customerWhatsApp || '',
      items: data.items || [],
      subtotal: data.subtotal || 0,
      taxAmount: data.taxAmount || 0,
      shippingCost: data.shippingCost || 0,
      discountAmount: data.discountAmount || 0,
      totalAmount: data.totalAmount || 0,
      status: data.status || 'pending',
      paymentStatus: data.paymentStatus || 'pending',
      paymentMethod: data.paymentMethod || '',
      shippingAddress: data.shippingAddress || '',
      billingAddress: data.billingAddress || data.shippingAddress || '',
      notes: data.notes || '',
      trackingNumber: data.trackingNumber || '',
      estimatedDelivery: toDate(data.estimatedDelivery),
      orderDate: toDate(data.orderDate) || new Date(),
      updatedAt: toDate(data.updatedAt) || new Date(),
      createdBy: data.createdBy || '',
      isWarehouseAllocated: data.isWarehouseAllocated || false,
      allocatedWarehouseId: data.allocatedWarehouseId || null,
      allocatedWarehouseName: data.allocatedWarehouseName || null,
      cancellationReason: data.cancellationReason || null,
      returnReason: data.returnReason || null,
      damagedItems: data.damagedItems || null,
      refundStatus: data.refundStatus ?? null,
      refundedAmount: data.refundedAmount ?? null,
      refundMethod: data.refundMethod ?? null,
      refundTransactionId: data.refundTransactionId ?? null,
      refundDate: toDate(data.refundDate) ?? null,
      refundNotes: data.refundNotes ?? null,
      // Payment proof attachment
      paymentProofUrl: data.paymentProofUrl ?? null,
      paymentProofType: data.paymentProofType ?? null,
      paymentProofFileName: data.paymentProofFileName ?? null,
    };

    return order as Order;
  }

  /* ===================== PAYMENT PROOF — UPLOAD ===========================
   * Uploads a payment proof file (image or PDF) to Firebase Storage under
   * the path:  order_payment_proofs/{orderId}/{timestamp}_{filename}
   *
   * Returns the public download URL and the detected file type.
   * ======================================================================= */
  static async uploadPaymentProof(
    file: File,
    orderId: string,
    onProgress?: (percent: number) => void
  ): Promise<{ url: string; type: 'image' | 'pdf'; fileName: string }> {
    const timestamp = Date.now();
    const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const storagePath = `order_payment_proofs/${orderId}/${timestamp}_${safeFileName}`;
    const storageRef = ref(storage, storagePath);

    return new Promise((resolve, reject) => {
      const uploadTask = uploadBytesResumable(storageRef, file, {
        contentType: file.type,
      });

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          if (onProgress) {
            const percent = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
            onProgress(percent);
          }
        },
        (error) => {
          console.error('Payment proof upload error:', error);
          reject(new Error(`Upload failed: ${error.message}`));
        },
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            const type: 'image' | 'pdf' = file.type === 'application/pdf' ? 'pdf' : 'image';
            resolve({ url, type, fileName: file.name });
          } catch (err) {
            reject(new Error('Failed to get download URL after upload'));
          }
        }
      );
    });
  }

  /* ===================== PAYMENT PROOF — DELETE ===========================
   * Deletes the payment proof file from Firebase Storage using the stored
   * download URL.  Silently succeeds if the file no longer exists.
   * ======================================================================= */
  static async deletePaymentProof(downloadUrl: string): Promise<void> {
    try {
      const storageRef = ref(storage, downloadUrl);
      await deleteObject(storageRef);
    } catch (error: any) {
      // 'object-not-found' means it was already deleted — treat as success.
      if (error?.code === 'storage/object-not-found') return;
      console.error('Error deleting payment proof:', error);
      throw new Error(`Failed to delete payment proof: ${error.message}`);
    }
  }

  /* ===================== PAYMENT PROOF — SAVE TO ORDER ====================
   * Persists payment proof metadata (url, type, fileName) onto an existing
   * order document.  Called after a successful upload.
   * ======================================================================= */
  static async savePaymentProofToOrder(
    orderId: string,
    proofData: { url: string; type: 'image' | 'pdf'; fileName: string } | null
  ): Promise<void> {
    try {
      const orderRef = doc(db, ORDERS_COLLECTION, orderId);
      await updateDoc(orderRef, {
        paymentProofUrl: proofData?.url ?? null,
        paymentProofType: proofData?.type ?? null,
        paymentProofFileName: proofData?.fileName ?? null,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error saving payment proof to order:', error);
      throw error;
    }
  }

  /* ===================== GET ORDERS PAGINATED =====================
   *
   * FIX: Replaced cursor-based pagination (startAfter / endBefore /
   * limitToLast) with a two-step cursor-seek so any page can be fetched
   * in any order (page 1 → page 5 → page 2 all work correctly).
   *
   * HOW IT WORKS
   * ─────────────
   * Firestore's client SDK has no offset(). The classic startAfter cursor
   * only works sequentially. To jump to page N we:
   *
   *   Step 1 — fetch only the first (page-1)*pageSize doc IDs (lightweight
   *             seek). The last doc from this set becomes our startAfter cursor.
   *   Step 2 — fetch the real pageSize docs starting after that cursor.
   *
   * For page 1 the seek is skipped entirely (skip count = 0).
   * ========================================================= */
  static async getOrdersPaginated(
    pageSize: number = 10,
    page: number = 1,
    searchQuery?: string,
    statusFilter?: string,
    paymentStatusFilter?: string,
    warehouseFilter?: string,
    dateFromFilter?: string,
    dateToFilter?: string
  ): Promise<PaginatedOrdersResult> {
    try {
      const orders: Order[] = [];
      const collectionRef = collection(db, ORDERS_COLLECTION);

      // ── Build filter constraints (where clauses only, no ordering/paging) ──
      const filterConstraints: any[] = [];

      if (statusFilter && statusFilter !== 'all') {
        filterConstraints.push(where('status', '==', statusFilter));
      }
      if (paymentStatusFilter && paymentStatusFilter !== 'all') {
        filterConstraints.push(where('paymentStatus', '==', paymentStatusFilter));
      }
      if (warehouseFilter && warehouseFilter !== 'all') {
        filterConstraints.push(where('allocatedWarehouseId', '==', warehouseFilter));
      }
      if (dateFromFilter) {
        const fromDate = new Date(dateFromFilter);
        fromDate.setHours(0, 0, 0, 0);
        filterConstraints.push(where('orderDate', '>=', Timestamp.fromDate(fromDate)));
      }
      if (dateToFilter) {
        const toDate = new Date(dateToFilter);
        toDate.setHours(23, 59, 59, 999);
        filterConstraints.push(where('orderDate', '<=', Timestamp.fromDate(toDate)));
      }

      const orderConstraint = orderBy('orderDate', 'desc');

      // ── Total count (0 doc reads) ──────────────────────────────────────────
      const countSnapshot = await getCountFromServer(
        query(collectionRef, ...filterConstraints)
      );
      const totalCount = countSnapshot.data().count;

      // ── Step 1: cursor seek ────────────────────────────────────────────────
      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, ...filterConstraints, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      // ── Step 2: fetch the real page ────────────────────────────────────────
      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, ...filterConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, ...filterConstraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      // ── Map + client-side search filter ───────────────────────────────────
      snapshot.docs.forEach(docSnap => {
        const order = this.mapDocToOrder(docSnap);
        if (searchQuery && searchQuery.trim() !== '') {
          const searchLower = searchQuery.toLowerCase();
          const matchesSearch =
            order.orderNumber.toLowerCase().includes(searchLower) ||
            order.customerName.toLowerCase().includes(searchLower) ||
            order.customerEmail.toLowerCase().includes(searchLower) ||
            (order.customerPhone && order.customerPhone.toLowerCase().includes(searchLower)) ||
            (order.trackingNumber && order.trackingNumber.toLowerCase().includes(searchLower)) ||
            (order.items && order.items.some(item =>
              item.productName.toLowerCase().includes(searchLower) ||
              (item.productSku && item.productSku.toLowerCase().includes(searchLower))
            ));
          if (matchesSearch) orders.push(order);
        } else {
          orders.push(order);
        }
      });

      return {
        orders,
        totalCount,
        lastVisible: null,   // no longer used by callers
        firstVisible: null,  // no longer used by callers
      };
    } catch (error) {
      console.error('Error fetching paginated orders:', error);
      throw error;
    }
  }

  static async generateOrderNumber(): Promise<string> {
    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substring(2, 5).toUpperCase();
    return `ORD-${timestamp}-${randomSuffix}`;
  }

  static extractVariantIdFromKey(variantKey: string): string {
    if (variantKey.startsWith('id:')) return variantKey.substring(3);
    return variantKey;
  }

  static getVariantKey(v: any): string {
    if (v.id !== undefined && v.id !== null && v.id !== '') return `id:${v.id}`;
    if (v.sku && v.sku.trim() !== '') return `sku:${v.sku.trim()}`;
    if (v.variantSelections && v.variantSelections.length > 0) {
      const selections = v.variantSelections
        .map((s: any) => `${s.variantType}:${s.variantValue}`)
        .sort()
        .join('|');
      return `sel:${selections}`;
    }
    return `fallback:${Math.random().toString(36).slice(2, 9)}`;
  }

  static findVariantByKey(variants: any[], key: string): any | undefined {
    return variants.find(v => this.getVariantKey(v) === key);
  }

  static getVariantDisplayName(variant: any): string {
    if (!variant.variantSelections || variant.variantSelections.length === 0) return 'Default Variant';
    return variant.variantSelections
      .map((selection: any) => `${selection.variantType}: ${selection.variantValue}`)
      .join(', ');
  }

  static async checkStockAvailabilityAcrossWarehouses(
    items: { productId: string; variantId?: string; quantity: string }[]
  ): Promise<{
    isValid: boolean;
    errors: string[];
    availability: ProductStockAvailability[];
    suggestedWarehouse?: string;
  }> {
    const errors: string[] = [];
    const availability: ProductStockAvailability[] = [];
    const warehousesByProduct: Map<string, Set<string>> = new Map();

    for (const item of items) {
      try {
        const product = await ProductService.getProduct(item.productId);
        if (!product) { errors.push(`Product not found: ${item.productId}`); continue; }

        let variant = null;
        if (product.hasVariants && product.variants && product.variants.length > 0 && item.variantId) {
          const actualVariantId = this.extractVariantIdFromKey(item.variantId);
          variant = product.variants.find((v: any) => v.id === actualVariantId) ||
            this.findVariantByKey(product.variants, item.variantId) ||
            product.variants.find((v: any) => v.sku === item.variantId);
          if (!variant) { errors.push(`Variant not found for product: ${product.name}`); continue; }
        }

        const requestedQuantity = parseInt(item.quantity);
        let totalAvailable = 0;
        const warehouses: WarehouseStockInfo[] = [];
        const allWarehouses = await WarehouseService.getActiveWarehouses();

        for (const warehouse of allWarehouses) {
          try {
            const inventory = await WarehouseInventoryService.getProductInventory(
              warehouse.id!, item.productId, variant?.id
            );
            if (inventory) {
              const availableQuantity = inventory.quantity || 0;
              totalAvailable += availableQuantity;
              warehouses.push({
                warehouseId: warehouse.id!,
                warehouseName: warehouse.name,
                availableQuantity,
                totalStock: warehouse.capacity.total,
              });
              const key = `${item.productId}_${variant?.id || 'simple'}`;
              if (!warehousesByProduct.has(key)) warehousesByProduct.set(key, new Set());
              warehousesByProduct.get(key)!.add(warehouse.id!);
            }
          } catch (err) {
            console.warn(`Error checking inventory for warehouse ${warehouse.id}:`, err);
          }
        }

        const canFulfill = totalAvailable >= requestedQuantity;
        availability.push({
          productId: item.productId,
          variantId: variant?.id,
          productName: product.name,
          variantName: variant ? this.getVariantDisplayName(variant) : undefined,
          requestedQuantity,
          warehouses,
          canFulfill,
        });

        if (!canFulfill) {
          errors.push(
            `Insufficient stock for ${product.name}${variant ? ` - ${this.getVariantDisplayName(variant)}` : ''}. ` +
            `Available: ${totalAvailable}, Requested: ${requestedQuantity}`
          );
        }
      } catch (error) {
        errors.push(`Error checking stock for item: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    let suggestedWarehouse: string | undefined;
    if (warehousesByProduct.size > 0) {
      const allWarehouseSets = Array.from(warehousesByProduct.values());
      const commonWarehouses = allWarehouseSets.reduce((intersection, set) => {
        return new Set(Array.from(intersection).filter(x => set.has(x)));
      }, allWarehouseSets[0]);
      if (commonWarehouses.size > 0) suggestedWarehouse = Array.from(commonWarehouses)[0];
    }

    return { isValid: errors.length === 0, errors, availability, suggestedWarehouse };
  }

  static async allocateOrderToWarehouses(
    orderId: string,
    itemAllocations: ItemWarehouseAllocation[],
    orderItems: OrderItem[]
  ): Promise<void> {
    try {
      await runTransaction(db, async (transaction) => {
        const warehouseRefs = new Map<string, any>();
        const warehouseDocs = new Map<string, any>();
        const uniqueWarehouseIds = new Set<string>();
        itemAllocations.forEach(item => {
          item.allocations.forEach(allocation => uniqueWarehouseIds.add(allocation.warehouseId));
        });

        for (const warehouseId of Array.from(uniqueWarehouseIds)) {
          const warehouseRef = doc(db, 'warehouses', warehouseId);
          const warehouseDoc = await transaction.get(warehouseRef);
          if (!warehouseDoc.exists()) throw new Error(`Warehouse ${warehouseId} not found`);
          warehouseRefs.set(warehouseId, warehouseRef);
          warehouseDocs.set(warehouseId, warehouseDoc.data());
        }

        const inventoryReads: Array<{
          ref: any; doc: any;
          allocation: ItemWarehouseAllocation['allocations'][0];
          itemAllocation: ItemWarehouseAllocation;
        }> = [];

        for (const itemAllocation of itemAllocations) {
          for (const allocation of itemAllocation.allocations) {
            const productPath = itemAllocation.variantId
              ? `${itemAllocation.productId}_${itemAllocation.variantId}`
              : itemAllocation.productId;
            const inventoryRef = doc(db, 'warehouse_inventory', allocation.warehouseId, 'products', productPath);
            const inventoryDoc = await transaction.get(inventoryRef);
            if (!inventoryDoc.exists()) {
              throw new Error(`Product ${itemAllocation.productName} not found in warehouse ${allocation.warehouseName}`);
            }
            inventoryReads.push({ ref: inventoryRef, doc: inventoryDoc, allocation, itemAllocation });
          }
        }

        for (const { doc: inventoryDoc, allocation, itemAllocation } of inventoryReads) {
          const currentStock = inventoryDoc.data().quantity || 0;
          if (currentStock < allocation.quantity) {
            throw new Error(
              `Insufficient stock in ${allocation.warehouseName} for ${itemAllocation.productName}. ` +
              `Available: ${currentStock}, Requested: ${allocation.quantity}`
            );
          }
        }

        const warehouseQuantityDeductions = new Map<string, number>();
        for (const { ref: inventoryRef, doc: inventoryDoc, allocation } of inventoryReads) {
          const inventoryData = inventoryDoc.data();
          const newStock = (inventoryData?.quantity || 0) - allocation.quantity;
          debugger
          transaction.update(inventoryRef, {
            quantity: newStock,
            isOutOfStock: newStock === 0,
            isLowStock: newStock > 0 && newStock <= (inventoryData?.minStockLevel || 0),
            lastUpdated: serverTimestamp(),
          });
          const current = warehouseQuantityDeductions.get(allocation.warehouseId) || 0;
          warehouseQuantityDeductions.set(allocation.warehouseId, current + allocation.quantity);
        }

        for (const [warehouseId, totalDeduction] of Array.from(warehouseQuantityDeductions.entries())) {
          const warehouseRef = warehouseRefs.get(warehouseId);
          const warehouseData = warehouseDocs.get(warehouseId);
          transaction.update(warehouseRef, {
            'capacity.used': (warehouseData.capacity?.used || 0) - totalDeduction,
            'capacity.available': (warehouseData.capacity?.available || 0) + totalDeduction,
            'stats.totalStock': (warehouseData.stats?.totalStock || 0) - totalDeduction,
            updatedAt: serverTimestamp(),
          });
        }

        const updatedItems: any[] = [];
        for (const orderItem of orderItems) {
          const itemAllocation = itemAllocations.find(
            ia => ia.productId === orderItem.productId &&
              (ia.variantId === orderItem.variantId || (!ia.variantId && !orderItem.variantId))
          );
          if (!itemAllocation) throw new Error(`No allocation found for product: ${orderItem.productName}`);

          for (const allocation of itemAllocation.allocations) {
            const warehouseData = warehouseDocs.get(allocation.warehouseId);
            updatedItems.push(cleanForFirestore({
              productId: orderItem.productId, productName: orderItem.productName,
              productSku: orderItem.productSku, productImage: orderItem.productImage || null,
              quantity: allocation.quantity, unitPrice: orderItem.unitPrice,
              totalPrice: orderItem.unitPrice * allocation.quantity,
              variantId: orderItem.variantId || null,
              variantSelections: orderItem.variantSelections || null,
              variantSku: orderItem.variantSku || null,
              sourceWarehouseId: allocation.warehouseId,
              sourceWarehouseName: warehouseData?.name || allocation.warehouseName,
            }));
          }
        }

        const orderRef = doc(db, ORDERS_COLLECTION, orderId);
        transaction.update(orderRef, cleanForFirestore({
          items: updatedItems, isWarehouseAllocated: true, updatedAt: serverTimestamp(),
        }));
      });
    } catch (error) {
      console.error('Error allocating order to warehouses:', error);
      throw error;
    }
  }

  static async restoreStockToWarehouse(
    orderId: string,
    items: OrderItem[],
    reason: CancellationReason | ReturnReason,
    notes?: string
  ): Promise<void> {
    try {
      await runTransaction(db, async (transaction) => {
        const damagedItems: DamagedItem[] = [];

        for (const item of items) {
          if (!item.sourceWarehouseId) {
            console.warn(`Item ${item.productId} has no source warehouse, skipping stock restoration`);
            continue;
          }
          const warehouseRef = doc(db, 'warehouses', item.sourceWarehouseId);
          const inventoryRef = doc(
            db, 'warehouse_inventory', item.sourceWarehouseId, 'products',
            item.variantId ? `${item.productId}_${item.variantId}` : item.productId
          );
          const [warehouseDoc, inventoryDoc] = await Promise.all([
            transaction.get(warehouseRef),
            transaction.get(inventoryRef),
          ]);

          if (!inventoryDoc.exists()) {
            console.warn(`Inventory record not found for ${item.productId} in warehouse ${item.sourceWarehouseId}`);
            continue;
          }

          const warehouseData = warehouseDoc.exists() ? warehouseDoc.data() : null;
          const inventoryData = inventoryDoc.data();
          const isDamaged = (reason as ReturnReason) === 'damaged' || (reason as ReturnReason) === 'defective';

          if (isDamaged) {
            damagedItems.push({
              productId: item.productId, variantId: item.variantId,
              productName: item.productName, quantity: item.quantity,
              reason: reason as ReturnReason, notes,
            });
          } else {
            debugger
            const newStock = (inventoryData.quantity || 0) + item.quantity;
            transaction.update(inventoryRef, {
              quantity: newStock,
              isOutOfStock: newStock === 0,
              isLowStock: newStock > 0 && newStock <= inventoryData.minStockLevel,
              lastUpdated: serverTimestamp(),
            });
            if (warehouseData) {
              transaction.update(warehouseRef, {
                'capacity.used': warehouseData.capacity.used + item.quantity,
                'capacity.available': warehouseData.capacity.available - item.quantity,
                'stats.totalStock': warehouseData.stats?.totalStock
                  ? warehouseData.stats.totalStock + item.quantity
                  : item.quantity,
                updatedAt: serverTimestamp(),
              });
            }
          }
        }

        if (damagedItems.length > 0) {
          const damagedItemsRef = doc(collection(db, DAMAGED_ITEMS_COLLECTION));
          transaction.set(damagedItemsRef, { orderId, items: damagedItems, reason, notes, createdAt: serverTimestamp() });
        }

        const orderRef = doc(db, ORDERS_COLLECTION, orderId);
        const updateData: any = { updatedAt: serverTimestamp() };
        if (damagedItems.length > 0) updateData.damagedItems = damagedItems;
        transaction.update(orderRef, updateData);
      });
    } catch (error) {
      console.error('Error restoring stock to warehouse:', error);
      throw error;
    }
  }

  static async createOrder(
    formData: OrderFormData,
    userId: string,
    warehouseId?: string,
    itemAllocations?: ItemWarehouseAllocation[]
  ): Promise<{ orderId: string; needsAllocation: boolean }> {
    try {
      const orderNumber = await this.generateOrderNumber();
      const orderItems = await this.validateOrderItems(formData.items);

      const taxAmount = parseFloat(formData.taxAmount) || 0;
      const shippingCost = parseFloat(formData.shippingCost) || 0;
      const discountAmount = parseFloat(formData.discountAmount) || 0;

      const { subtotal, totalAmount } = this.calculateOrderTotals(orderItems, taxAmount, shippingCost, discountAmount);
      const currentTimestamp = Timestamp.fromDate(new Date());

      const orderData: any = {
        orderNumber,
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone || null,
        customerWhatsApp: formData.customerWhatsApp || null,
        items: orderItems.map(item => ({
          productId: item.productId,
          productName: item.productName,
          productSku: item.productSku,
          productImage: item.productImage || null,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          totalPrice: item.totalPrice,
          variantId: item.variantId || null,
          variantSelections: item.variantSelections || null,
          variantSku: item.variantSku || null,
          sourceWarehouseId: null,
          sourceWarehouseName: null,
        })),
        subtotal, taxAmount, shippingCost, discountAmount, totalAmount,
        status: formData.status,
        paymentStatus: formData.paymentStatus,
        paymentMethod: formData.paymentMethod,
        shippingAddress: formData.shippingAddress,
        billingAddress: formData.billingAddress || formData.shippingAddress,
        notes: formData.notes || null,
        trackingNumber: formData.trackingNumber || null,
        estimatedDelivery: formData.estimatedDelivery
          ? Timestamp.fromDate(new Date(formData.estimatedDelivery))
          : null,
        orderDate: currentTimestamp,
        updatedAt: currentTimestamp,
        createdBy: userId,
        isWarehouseAllocated: false,
        allocatedWarehouseId: null,
        allocatedWarehouseName: null,
        cancellationReason: null,
        returnReason: null,
        damagedItems: null,
        refundStatus: null,
        refundedAmount: null,
        refundMethod: null,
        refundTransactionId: null,
        refundDate: null,
        refundNotes: null,
        // Payment proof — included if already uploaded before order creation
        paymentProofUrl: formData.paymentProofUrl ?? null,
        paymentProofType: formData.paymentProofType ?? null,
        paymentProofFileName: formData.paymentProofFileName ?? null,
      };

      const cleanedOrderData = cleanForFirestore(orderData);

      if (formData.status === 'pending') {
        const docRef = await addDoc(collection(db, ORDERS_COLLECTION), cleanedOrderData);
        return { orderId: docRef.id, needsAllocation: true };
      }

      const stockCheck = await this.checkStockAvailabilityAcrossWarehouses(formData.items);
      if (!stockCheck.isValid) {
        throw new Error(`Stock availability check failed: ${stockCheck.errors.join(', ')}`);
      }
      if (!itemAllocations || itemAllocations.length === 0) {
        throw new Error('Warehouse allocation is required for non-pending orders');
      }

      return await runTransaction(db, async (transaction) => {
        const warehouseRefs = new Map<string, any>();
        const warehouseDocs = new Map<string, any>();
        const uniqueWarehouseIds = new Set<string>();
        itemAllocations.forEach(item => {
          item.allocations.forEach(allocation => uniqueWarehouseIds.add(allocation.warehouseId));
        });

        for (const wId of Array.from(uniqueWarehouseIds)) {
          const warehouseRef = doc(db, 'warehouses', wId);
          const warehouseDoc = await transaction.get(warehouseRef);
          if (!warehouseDoc.exists()) throw new Error(`Warehouse ${wId} not found`);
          warehouseRefs.set(wId, warehouseRef);
          warehouseDocs.set(wId, warehouseDoc.data());
        }

        const inventoryReads: Array<{
          ref: any; doc: any;
          allocation: ItemWarehouseAllocation['allocations'][0];
          itemAllocation: ItemWarehouseAllocation;
        }> = [];

        for (const itemAllocation of itemAllocations) {
          for (const allocation of itemAllocation.allocations) {
            const productPath = itemAllocation.variantId
              ? `${itemAllocation.productId}_${itemAllocation.variantId}`
              : itemAllocation.productId;
            const inventoryRef = doc(db, 'warehouse_inventory', allocation.warehouseId, 'products', productPath);
            const inventoryDoc = await transaction.get(inventoryRef);
            if (!inventoryDoc.exists()) {
              throw new Error(`Product ${itemAllocation.productName} not found in warehouse ${allocation.warehouseName}`);
            }
            inventoryReads.push({ ref: inventoryRef, doc: inventoryDoc, allocation, itemAllocation });
          }
        }

        for (const { doc: inventoryDoc, allocation, itemAllocation } of inventoryReads) {
          const currentStock = inventoryDoc.data().quantity || 0;
          if (currentStock < allocation.quantity) {
            throw new Error(
              `Insufficient stock in ${allocation.warehouseName} for ${itemAllocation.productName}. ` +
              `Available: ${currentStock}, Requested: ${allocation.quantity}`
            );
          }
        }

        const orderRef = doc(collection(db, ORDERS_COLLECTION));

        const updatedItems: any[] = [];
        for (const orderItem of orderItems) {
          const itemAllocation = itemAllocations.find(
            ia => ia.productId === orderItem.productId &&
              (ia.variantId === orderItem.variantId || (!ia.variantId && !orderItem.variantId))
          );
          if (!itemAllocation) throw new Error(`No allocation found for product: ${orderItem.productName}`);

          for (const allocation of itemAllocation.allocations) {
            const warehouseData = warehouseDocs.get(allocation.warehouseId);
            updatedItems.push(cleanForFirestore({
              productId: orderItem.productId, productName: orderItem.productName,
              productSku: orderItem.productSku, productImage: orderItem.productImage || null,
              quantity: allocation.quantity, unitPrice: orderItem.unitPrice,
              totalPrice: orderItem.unitPrice * allocation.quantity,
              variantId: orderItem.variantId || null,
              variantSelections: orderItem.variantSelections || null,
              variantSku: orderItem.variantSku || null,
              sourceWarehouseId: allocation.warehouseId,
              sourceWarehouseName: warehouseData?.name || allocation.warehouseName,
            }));
          }
        }

        transaction.set(orderRef, cleanForFirestore({
          ...cleanedOrderData, items: updatedItems, isWarehouseAllocated: true,
        }));

        const warehouseQuantityDeductions = new Map<string, number>();
        for (const { ref: inventoryRef, doc: inventoryDoc, allocation } of inventoryReads) {
          const inventoryData = inventoryDoc.data();
          const newStock = (inventoryData?.quantity || 0) - allocation.quantity;
          debugger
          transaction.update(inventoryRef, {
            quantity: newStock,
            isOutOfStock: newStock === 0,
            isLowStock: newStock > 0 && newStock <= (inventoryData?.minStockLevel || 0),
            lastUpdated: serverTimestamp(),
          });
          const current = warehouseQuantityDeductions.get(allocation.warehouseId) || 0;
          warehouseQuantityDeductions.set(allocation.warehouseId, current + allocation.quantity);
        }

        for (const [wId, totalDeduction] of Array.from(warehouseQuantityDeductions.entries())) {
          const warehouseRef = warehouseRefs.get(wId);
          const warehouseData = warehouseDocs.get(wId);
          transaction.update(warehouseRef, {
            'capacity.used': (warehouseData.capacity?.used || 0) - totalDeduction,
            'capacity.available': (warehouseData.capacity?.available || 0) + totalDeduction,
            'stats.totalStock': (warehouseData.stats?.totalStock || 0) - totalDeduction,
            updatedAt: serverTimestamp(),
          });
        }

        return { orderId: orderRef.id, needsAllocation: false };
      });
    } catch (error) {
      console.error('Error creating order:', error);
      if (error instanceof Error) {
        if (error.message.includes('insufficient')) throw new Error(`Stock unavailable: ${error.message}`);
        if (error.message.includes('not found')) throw new Error(`Inventory error: ${error.message}`);
        if (error.message.includes('transaction')) throw new Error('Transaction failed. Please try again.');
      }
      throw new Error(`Failed to create order: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  static async updateOrder(
    orderId: string,
    formData: OrderFormData,
    cancellationReason?: CancellationReason,
    returnReason?: ReturnReason,
    damageNotes?: string
  ): Promise<void> {
    try {
      const orderRef = doc(db, ORDERS_COLLECTION, orderId);
      const existingOrder = await this.getOrder(orderId);
      if (!existingOrder) throw new Error('Order not found');

      const oldStatus = existingOrder.status;
      const newStatus = formData.status;
      const orderItems = await this.validateOrderItems(formData.items);

      if (oldStatus !== newStatus) {
        if (newStatus === 'cancelled' && oldStatus !== 'cancelled') {
          if (!cancellationReason) throw new Error('Cancellation reason is required');
          if (existingOrder.isWarehouseAllocated) {
            await this.restoreStockToWarehouse(orderId, existingOrder.items, cancellationReason);
          }
        }
        if (newStatus === 'returned' && oldStatus !== 'returned') {
          if (!returnReason) throw new Error('Return reason is required');
          if (existingOrder.isWarehouseAllocated) {
            await this.restoreStockToWarehouse(orderId, existingOrder.items, returnReason, damageNotes);
          }
        }
      }

      const taxAmount = parseFloat(formData.taxAmount) || 0;
      const shippingCost = parseFloat(formData.shippingCost) || 0;
      const discountAmount = parseFloat(formData.discountAmount) || 0;
      const { subtotal, totalAmount } = this.calculateOrderTotals(orderItems, taxAmount, shippingCost, discountAmount);

      const updateData: any = {
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone || null,
        customerWhatsApp: formData.customerWhatsApp || null,
        // items: orderItems,
        items: existingOrder.isWarehouseAllocated ? existingOrder.items : orderItems,
        subtotal, taxAmount, shippingCost, discountAmount, totalAmount,
        status: formData.status,
        paymentStatus: formData.paymentStatus,
        paymentMethod: formData.paymentMethod,
        shippingAddress: formData.shippingAddress,
        billingAddress: formData.billingAddress,
        notes: formData.notes || null,
        trackingNumber: formData.trackingNumber || null,
        estimatedDelivery: formData.estimatedDelivery
          ? Timestamp.fromDate(new Date(formData.estimatedDelivery))
          : null,
        // Payment proof — always sync whatever is in formData
        paymentProofUrl: formData.paymentProofUrl ?? null,
        paymentProofType: formData.paymentProofType ?? null,
        paymentProofFileName: formData.paymentProofFileName ?? null,
        updatedAt: serverTimestamp(),
      };

      if (cancellationReason) updateData.cancellationReason = cancellationReason;
      if (returnReason) updateData.returnReason = returnReason;

      await updateDoc(orderRef, cleanForFirestore(updateData));
    } catch (error) {
      console.error('Error updating order:', error);
      throw error;
    }
  }

  static async manuallyAllocateOrder(
    orderId: string,
    itemAllocations: ItemWarehouseAllocation[]
  ): Promise<void> {
    try {
      const order = await this.getOrder(orderId);
      if (!order) throw new Error('Order not found');
      if (order.isWarehouseAllocated) throw new Error('Order is already allocated to warehouses');
      if (order.status !== 'pending') throw new Error('Only pending orders can be manually allocated');

      const orderItems = await this.validateOrderItems(
        order.items.map(item => ({
          productId: item.productId,
          variantId: item.variantId,
          quantity: item.quantity.toString(),
        }))
      );

      await this.allocateOrderToWarehouses(orderId, itemAllocations, orderItems);
      const orderRef = doc(db, ORDERS_COLLECTION, orderId);
      await updateDoc(orderRef, { status: 'processing', updatedAt: serverTimestamp() });
    } catch (error) {
      console.error('Error manually allocating order:', error);
      throw error;
    }
  }

  static async getDamagedItems(): Promise<DamagedItemRecord[]> {
    try {
      const q = query(collection(db, DAMAGED_ITEMS_COLLECTION), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          orderId: data.orderId || '',
          items: data.items || [],
          reason: data.reason || 'other',
          notes: data.notes || '',
          createdAt: data.createdAt?.toDate() || new Date(),
        } as DamagedItemRecord;
      });
    } catch (error) {
      console.error('Error fetching damaged items:', error);
      throw error;
    }
  }

  static async validateOrderItems(
    items: { productId: string; variantId?: string; quantity: string }[]
  ): Promise<OrderItem[]> {
    const orderItems: OrderItem[] = [];

    for (const item of items) {
      if (!item.productId || !item.quantity || parseInt(item.quantity) <= 0) {
        throw new Error('Invalid item data');
      }

      const product = await ProductService.getProduct(item.productId);
      if (!product) throw new Error(`Product not found: ${item.productId}`);

      let selectedVariant = null;
      let unitPrice = product.price;
      let sku = product.sku;
      let variantSelections: any[] = [];

      if (product.hasVariants && product.variants && product.variants.length > 0) {
        if (!item.variantId) throw new Error(`Variant selection required for product: ${product.name}`);

        const actualVariantId = this.extractVariantIdFromKey(item.variantId);
        selectedVariant = product.variants.find((v: any) => v.id === actualVariantId) ||
          this.findVariantByKey(product.variants, item.variantId) ||
          product.variants.find((v: any) => v.sku === item.variantId);

        if (!selectedVariant) {
          throw new Error(`Variant not found for product: ${product.name}. Please reselect the variant.`);
        }

        unitPrice = selectedVariant.price;
        sku = selectedVariant.sku;
        variantSelections = selectedVariant.variantSelections || [];
      }

      const quantity = parseInt(item.quantity);
      const totalPrice = quantity * unitPrice;

      orderItems.push({
        productId: product.id!,
        productName: product.name,
        productSku: sku,
        productImage: product.images[0] || undefined,
        quantity,
        unitPrice,
        totalPrice,
        variantId: selectedVariant?.id || undefined,
        variantSelections,
        variantSku: selectedVariant ? sku : undefined,
      });
    }

    return orderItems;
  }

  static async deductStockFromOrder(items: OrderItem[]): Promise<void> {
    try {
      await runTransaction(db, async (transaction) => {
        for (const item of items) {
          const productRef = doc(db, 'products', item.productId);
          const productDoc = await transaction.get(productRef);
          if (!productDoc.exists()) throw new Error(`Product not found: ${item.productId}`);
          const product = productDoc.data();

          if (item.variantId) {
            const updatedVariants = product.variants.map((variant: any) => {
              debugger
              if (variant.id === item.variantId) {
                const newStockQuantity = variant.stockQuantity - item.quantity;
                return { ...variant, stockQuantity: Math.max(0, newStockQuantity), isLowStock: newStockQuantity <= variant.minStockLevel, isOutOfStock: newStockQuantity === 0 };
              }
              return variant;
            });
            transaction.update(productRef, { variants: updatedVariants, updatedAt: serverTimestamp() });
          } else {
            debugger
            const newStockQuantity = product.stockQuantity - item.quantity;
            transaction.update(productRef, {
              stockQuantity: Math.max(0, newStockQuantity),
              isLowStock: newStockQuantity <= product.minStockLevel,
              isOutOfStock: newStockQuantity === 0,
              updatedAt: serverTimestamp(),
            });
          }
        }
      });
    } catch (error) {
      console.error('Error deducting stock:', error);
      throw new Error(`Failed to deduct stock: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  static async restoreStockFromOrder(items: OrderItem[]): Promise<void> {
    try {
      await runTransaction(db, async (transaction) => {
        for (const item of items) {
          const productRef = doc(db, 'products', item.productId);
          const productDoc = await transaction.get(productRef);
          if (!productDoc.exists()) { console.warn(`Product not found during stock restoration: ${item.productId}`); continue; }
          const product = productDoc.data();

          if (item.variantId) {
            debugger
            const updatedVariants = product.variants.map((variant: any) => {
              if (variant.id === item.variantId) {
                const newStockQuantity = variant.stockQuantity + item.quantity;
                return { ...variant, stockQuantity: newStockQuantity, isLowStock: newStockQuantity <= variant.minStockLevel, isOutOfStock: newStockQuantity === 0 };
              }
              return variant;
            });
            transaction.update(productRef, { variants: updatedVariants, updatedAt: serverTimestamp() });
          } else {
            debugger
            const newStockQuantity = product.stockQuantity + item.quantity;
            transaction.update(productRef, {
              stockQuantity: newStockQuantity,
              isLowStock: newStockQuantity <= product.minStockLevel,
              isOutOfStock: newStockQuantity === 0,
              updatedAt: serverTimestamp(),
            });
          }
        }
      });
    } catch (error) {
      console.error('Error restoring stock:', error);
      throw new Error(`Failed to restore stock: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  static calculateOrderTotals(
    items: OrderItem[],
    taxAmount: number,
    shippingCost: number,
    discountAmount: number
  ) {
    const subtotal = items.reduce((sum, item) => sum + item.totalPrice, 0);
    const totalAmount = Math.max(0, subtotal + taxAmount + shippingCost - discountAmount);
    return { subtotal, totalAmount };
  }

  static async deleteOrder(orderId: string): Promise<void> {
    try {
      const order = await this.getOrder(orderId);
      if (!order) throw new Error('Order not found');
      // Clean up payment proof from storage before deleting the order doc
      if (order.paymentProofUrl) {
        try {
          await this.deletePaymentProof(order.paymentProofUrl);
        } catch (err) {
          console.warn('Could not delete payment proof during order deletion:', err);
        }
      }
      if (order.isWarehouseAllocated) await this.restoreStockFromOrder(order.items);
      await deleteDoc(doc(db, ORDERS_COLLECTION, orderId));
    } catch (error) {
      console.error('Error deleting order:', error);
      throw error;
    }
  }

  /** @deprecated Use getOrdersPaginated instead */
  static async getOrders(): Promise<Order[]> {
    try {
      const q = query(collection(db, ORDERS_COLLECTION), orderBy('orderDate', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(docSnap => this.mapDocToOrder(docSnap));
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  }

  static async getOrder(orderId: string): Promise<Order | null> {
    try {
      const orderRef = doc(db, ORDERS_COLLECTION, orderId);
      const orderSnap = await getDoc(orderRef);
      if (orderSnap.exists()) return this.mapDocToOrder(orderSnap as QueryDocumentSnapshot<DocumentData>);
      return null;
    } catch (error) {
      console.error('Error fetching order:', error);
      throw error;
    }
  }

  static async getOrdersByStatus(status: string): Promise<Order[]> {
    try {
      const q = query(
        collection(db, ORDERS_COLLECTION),
        where('status', '==', status),
        orderBy('orderDate', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(docSnap => this.mapDocToOrder(docSnap));
    } catch (error) {
      console.error('Error fetching orders by status:', error);
      throw error;
    }
  }

  static async updateOrderStatus(orderId: string, status: string): Promise<void> {
    try {
      const order = await this.getOrder(orderId);
      if (!order) throw new Error('Order not found');
      const orderRef = doc(db, ORDERS_COLLECTION, orderId);
      if (status === 'cancelled' && order.status !== 'cancelled') {
        if (order.isWarehouseAllocated) await this.restoreStockFromOrder(order.items);
      } else if (order.status === 'cancelled' && status !== 'cancelled') {
        if (order.isWarehouseAllocated) await this.deductStockFromOrder(order.items);
      }
      await updateDoc(orderRef, { status, updatedAt: serverTimestamp() });
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  }

  static async updatePaymentStatus(orderId: string, paymentStatus: string): Promise<void> {
    try {
      await updateDoc(doc(db, ORDERS_COLLECTION, orderId), { paymentStatus, updatedAt: serverTimestamp() });
    } catch (error) {
      console.error('Error updating payment status:', error);
      throw error;
    }
  }

  /* ===================== GET ORDER STATS =====================
   * Uses getCountFromServer() for per-status counts (free reads).
   * Revenue requires a targeted getDocs for paid orders only — unavoidable
   * since Firestore has no SUM aggregate — but scoped to paid orders only.
   * ========================================================= */
  static async getOrderStats(): Promise<{
    total: number; pending: number; processing: number; shipped: number;
    delivered: number; cancelled: number; returned: number;
    totalRevenue: number; averageOrderValue: number;
  }> {
    try {
      const col = collection(db, ORDERS_COLLECTION);

      const [totalSnap, pendingSnap, processingSnap, shippedSnap, deliveredSnap, cancelledSnap, returnedSnap, paidSnap] =
        await Promise.all([
          getCountFromServer(query(col)),
          getCountFromServer(query(col, where('status', '==', 'pending'))),
          getCountFromServer(query(col, where('status', '==', 'processing'))),
          getCountFromServer(query(col, where('status', '==', 'shipped'))),
          getCountFromServer(query(col, where('status', '==', 'delivered'))),
          getCountFromServer(query(col, where('status', '==', 'cancelled'))),
          getCountFromServer(query(col, where('status', '==', 'returned'))),
          getDocs(query(col, where('paymentStatus', '==', 'paid'))),
        ]);

      const total = totalSnap.data().count;
      const totalRevenue = paidSnap.docs.reduce((sum, d) => sum + (d.data().totalAmount || 0), 0);

      return {
        total,
        pending: pendingSnap.data().count,
        processing: processingSnap.data().count,
        shipped: shippedSnap.data().count,
        delivered: deliveredSnap.data().count,
        cancelled: cancelledSnap.data().count,
        returned: returnedSnap.data().count,
        totalRevenue,
        averageOrderValue: total > 0 ? totalRevenue / total : 0,
      };
    } catch (error) {
      console.error('Error getting order stats:', error);
      throw error;
    }
  }

  static async checkStockAvailability(
    items: { productId: string; variantId?: string; quantity: string }[]
  ): Promise<{ isValid: boolean; errors: string[] }> {
    const errors: string[] = [];
    for (const item of items) {
      try {
        const product = await ProductService.getProduct(item.productId);
        if (!product) { errors.push(`Product not found: ${item.productId}`); continue; }

        if (product.hasVariants && product.variants && product.variants.length > 0 && item.variantId) {
          const actualVariantId = this.extractVariantIdFromKey(item.variantId);
          const variant = product.variants.find((v: any) => v.id === actualVariantId) ||
            this.findVariantByKey(product.variants, item.variantId) ||
            product.variants.find((v: any) => v.sku === item.variantId);
          if (!variant) {
            errors.push(`Variant not found for product: ${product.name}`);
          } else if (variant.stockQuantity < parseInt(item.quantity)) {
            errors.push(
              `Insufficient stock for ${product.name} - ${this.getVariantDisplayName(variant)}. ` +
              `Available: ${variant.stockQuantity}, Requested: ${item.quantity}`
            );
          }
        } else if (!product.hasVariants && product.stockQuantity < parseInt(item.quantity)) {
          errors.push(
            `Insufficient stock for ${product.name}. ` +
            `Available: ${product.stockQuantity}, Requested: ${item.quantity}`
          );
        }
      } catch (error) {
        errors.push(`Error checking stock for item: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
    return { isValid: errors.length === 0, errors };
  }

  static async updateOrderRefundInfo(
    orderId: string,
    info: {
      refundStatus: string;
      refundedAmount: number;
      refundMethod: string;
      refundTransactionId?: string;
      refundDate?: Date;
      refundNotes?: string;
    }
  ): Promise<void> {
    try {
      const orderRef = doc(db, ORDERS_COLLECTION, orderId);
      const validRefundPaymentStatuses = ['pending', 'partially_refunded', 'refunded'];

      const updatePayload: Record<string, any> = {
        refundStatus: info.refundStatus,
        refundedAmount: info.refundedAmount,
        refundMethod: info.refundMethod,
        refundTransactionId: info.refundTransactionId ?? null,
        refundDate: info.refundDate ? Timestamp.fromDate(info.refundDate) : null,
        refundNotes: info.refundNotes ?? null,
        updatedAt: serverTimestamp(),
      };

      if (validRefundPaymentStatuses.includes(info.refundStatus)) {
        updatePayload.paymentStatus = info.refundStatus;
      }

      await updateDoc(orderRef, updatePayload);
    } catch (error) {
      console.error('Error updating order refund info:', error);
      throw error;
    }
  }
}